import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Truck, Search } from "lucide-react";
import httpClient from "../../../../../../api/httpClient";
import notify from "../../../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { toast } from "react-toastify";
import Table1 from "../../../../../../components/ACS-Dashboard/ACS-Blocks/TableSecondary";

const ReceiverCarrierSelectionModal = ({
  onClose,
  onConfirm,
  initiallySelectedCarrier,
  title = "Receiver Carrier Selection",
  subtitle = "Choose the Carrier that is going to ship to the receiver from the list below.",
  shipmentNumber,
  officeCode,
  receiverOfficeCode
}) => {
  // API response carriers
  const [carriers, setCarriers] = useState([]);
  const [selectedCarrier, setSelectedCarrier] = useState(initiallySelectedCarrier);
  const [query, setQuery] = useState("");

useEffect(() => {
  const fetchCarriers = async () => {
    try {
       const authToken = document.cookie
        .split("; ")
        .find((row) => row.startsWith("authToken="))
        ?.split("=")[1];

      if (!authToken) {
        console.error("Authorization token not found, contact your admin");
        return;
      }

      const res = await httpClient.get(
        `/api/organizations/get-carriers`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const mapped = Array.isArray(res.data)
        ? res.data.map((org) => ({
            code: org.officeCode,
            description: org.name,
          }))
        : [];
      setCarriers(mapped);
    } catch (err) {
      console.error("Error fetching carriers:", err);
      toast.error("Failed to fetch carriers.");
    }
  };

  fetchCarriers();
}, []);

  // Filter carriers based on query
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return carriers.filter((c) =>
      [c.code, c.description].filter(Boolean).map(String).some((s) => s.toLowerCase().includes(q))
    );
  }, [carriers, query]);

  const handleConfirm = async () => {
  if (!selectedCarrier) {
    toast.warn("Please select a carrier.");
    return;
  }

  try {
    const authToken = document.cookie
      .split("; ")
      .find((row) => row.startsWith("authToken="))
      ?.split("=")[1];

    if (!authToken) {
      console.error("Authorization token not found, contact your admin");
      return;
    }

    // Update the parties and the shipment with the selected carrier
    await httpClient.put(
      `/api/shipment-charges-update/shipmentNumber/${shipmentNumber}/update-parties`,
      null,
      {
        headers: {
          "Authorization": `Bearer ${authToken}`,
          "Office-Code": officeCode,
          "Carrier-Office-Code": selectedCarrier.code,
        },
      }
    );

    notify.success({
      
      title: "Carrier Assigned",
      message: `${selectedCarrier.code} — ${selectedCarrier.description}`,
    });

    // call onConfirm to update parent component state
    onConfirm?.(selectedCarrier);

    onClose?.();
    
    setTimeout(() => {
      window.location.reload();
    }, 2000);

  } catch (err) {
    console.error("Error updating carrier:", err);
    toast.error("Failed to update carrier.");
  }
};

  return (
    <motion.div
      className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center px-fluid-2"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-fluid-3 space-y-fluid-3"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
      >
        {/* Header */}
        <div className="flex flex-col items-center space-y-fluid-1">
          <div className="bg-[#1e2a74] rounded-full p-fluid-2 text-white">
            <Truck size={28} />
          </div>
          <h2 className="text-fluid-xl font-semibold text-[#111827]">{title}</h2>
          <p className="text-fluid-base text-gray-600 text-center">{subtitle}</p>
        </div>

        {/* Search input */}
        <div className="flex items-center gap-3">
          <label className="block text-fluid-sm font-medium text-gray-700 shrink-0">
            Carrier *
          </label>
          <div className="relative w-full">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Carriers"
              className="w-full border border-gray-300 rounded-xl pl-9 pr-3 py-2 text-fluid-sm"
            />
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#223688]" />
          </div>
        </div>

        {/* Table list */}
        <div className="max-h-72 overflow-y-auto">
          {query.trim() === "" ? (
              <div className="text-center text-gray-500 py-6">
                Start typing to search for carriers…
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center text-gray-500 py-6">
                No matching carriers found.
              </div>
            ) : (
          <Table1
            headers={["Code", "Description", "Selected"]}
            columnWidths={["18%", "18%", "18%"]}
            data={filtered}
            renderRow={(row) => {
              const isSelected =
                selectedCarrier &&
                selectedCarrier.code === row.code &&
                selectedCarrier.description === row.description;

              return (
                <>
                  <td
                    className={`px-fluid-3 py-fluid-2 text-fluid-sm text-[#030229] ${
                      isSelected ? "bg-blue-50" : ""
                    }`}
                    onClick={() => setSelectedCarrier(row)}
                  >
                    {row.code}
                  </td>
                  <td
                    className={`px-fluid-3 py-fluid-2 text-fluid-sm text-[#030229] ${
                      isSelected ? "bg-blue-50" : ""
                    }`}
                    onClick={() => setSelectedCarrier(row)}
                  >
                    {row.description}
                  </td>
                  <td className="px-fluid-3 py-fluid-2 text-fluid-sm">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="carrier"
                        checked={!!isSelected}
                        onChange={() => setSelectedCarrier(row)}
                        className="h-4 w-4 accent-[#1e2a74]"
                      />
                    </div>
                  </td>
                </>
              );
            }}
          />
          )}
        </div>

        {/* Footer buttons */}
        <div className="flex justify-end gap-fluid-2 pt-fluid-2 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-fluid-button-x py-fluid-button-y border border-gray-300 rounded text-gray-700 hover:bg-gray-100 text-fluid-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedCarrier}
            className={`px-fluid-button-x py-fluid-button-y text-fluid-sm rounded font-semibold transition ${
              !selectedCarrier
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#1e2a74] hover:bg-[#18225c] text-white"
            }`}
          >
            Confirm
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ReceiverCarrierSelectionModal;
